import PersonGroup from './groups/person_group';
import HeatmapItem from './items/heatmap_item';
import {
	getHolidayDates,
	getMinutesInWeekdays,
	getPersonGroups,
	getWeekdaysBetweenDates,
	isDateWithinPersonStartEndDate,
} from '../canvas-timeline/canvas_timeline_util';
import CompanyUtilizationGroup from './groups/company_utilization_group';
import CompanyUtilizationItem from './items/company_utilization_item';
import {LoadingGroup} from './groups/loading_group';
import {GROUP_BY} from './people_scheduling_header';
import PersonGroupingGroup from './groups/person_grouping_group';
import PersonGroupUtilizationItem from './items/person_group_utilization_item';
import {hasPermission, personIsClientUser} from '../../../forecast-app/shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../../Permissions';

function getAllocatedMinutes(
	personHeatMapData,
	holidaysIncludingDaysOff,
	personWorkingMinutes,
	dayData,
	minutesAllocated,
	minutesAvailable,
	overallStartDate,
	overallEndDate,
	todayDate,
	isInActualMode,
	person,
	isUsingResourceService
) {
	let heatmapDataStartDate, heatmapDataEndDate, heatmapDataMinutesAllocated, heatmapDataMinutesTimeOff;
	const startDate =
		isUsingResourceService && person.startDate ? Math.max(overallStartDate, person.startDate) : overallStartDate;
	const endDate = isUsingResourceService && person.endDate ? Math.min(overallEndDate, person.endDate) : overallEndDate;
	for (let i = startDate; i <= endDate; i++) {
		// If we dont have a relevant date range yet, or the current date range's enddate is before our loop date "i"
		if ((!heatmapDataStartDate && !heatmapDataEndDate) || (heatmapDataEndDate !== null && heatmapDataEndDate < i)) {
			// Find new relevant date range
			const foundHeatMap = personHeatMapData.find(
				dataArray =>
					(dataArray[1] >= i && dataArray[0] === null) ||
					(dataArray[1] >= i && dataArray[0] <= i) ||
					(dataArray[1] === null && dataArray[0] <= i)
			);
			if (foundHeatMap) {
				[heatmapDataStartDate, heatmapDataEndDate, heatmapDataMinutesAllocated, heatmapDataMinutesTimeOff] =
					foundHeatMap;
			}
		}
		if (heatmapDataStartDate || heatmapDataEndDate) {
			minutesAllocated += heatmapDataMinutesAllocated;
			if (heatmapDataEndDate >= todayDate || !isInActualMode) {
				// Calc the persons minutes available for this day
				const minutesAvailableForDay = holidaysIncludingDaysOff.includes(i)
					? 0
					: personWorkingMinutes[dayData[i].isoWeekday - 1];
				minutesAvailable -= Math.min(heatmapDataMinutesTimeOff, minutesAvailableForDay); // Subtract timeoff from available. Limit to minutesAvailableForDay.
			}
		}
	}

	return [minutesAllocated, minutesAvailable];
}

const composeCompanyUtilizationRow = (
	pageComponent,
	lightWeightData,
	group,
	stepDataArray,
	timelineStartDate,
	pixelsPerDay,
	timelineMinorStep
) => {
	const items = [];
	const {dayData} = pageComponent.props;
	let isInActualMode =
		!lightWeightData.company.isUsingSchedulingPlanMode && !lightWeightData.company.isUsingProjectAllocation;

	if (pageComponent.isCalculationSwitched) {
		isInActualMode = !isInActualMode;
	}

	for (const stepData of stepDataArray) {
		let minutesAllocated = 0;
		let minutesAvailable = 0;
		let {startDate, endDate} = stepData;

		for (const person of lightWeightData.persons) {
			if (!person.active) {
				continue;
			}
			if (personIsClientUser(person)) {
				continue;
			}
			if (
				lightWeightData.isUsingResourceService &&
				((person.startDate && endDate < person.startDate) || (person.endDate && startDate > person.endDate))
			) {
				continue;
			}
			if (
				!getPersonGroups(pageComponent.state.groups).find(group => group.id === person.id && group.filtered === false)
			) {
				continue;
			}

			const personWorkingHours = {
				monday: person.monday,
				tuesday: person.tuesday,
				wednesday: person.wednesday,
				thursday: person.thursday,
				friday: person.friday,
				saturday: person.saturday,
				sunday: person.sunday,
			};
			const personWorkingMinutes = [
				person.monday,
				person.tuesday,
				person.wednesday,
				person.thursday,
				person.friday,
				person.saturday,
				person.sunday,
			];
			const personHeatMapData = lightWeightData.heatMaps[person.id];
			const {holidaysIncludingDaysOff} = getHolidayDates(person, lightWeightData, personWorkingMinutes);
			const weekdaysInPeriod = getWeekdaysBetweenDates(startDate, endDate);
			for (const holidayDate of holidaysIncludingDaysOff.filter(date => date >= startDate && date <= endDate)) {
				weekdaysInPeriod[dayData[holidayDate].isoWeekday - 1]--;
			}
			for (let canvasDate = startDate; canvasDate <= endDate; canvasDate++) {
				if (!isDateWithinPersonStartEndDate(person, canvasDate)) {
					weekdaysInPeriod[dayData[canvasDate].isoWeekday - 1] = Math.max(
						weekdaysInPeriod[dayData[canvasDate].isoWeekday - 1] - 1,
						0
					);
				}
			}
			minutesAvailable += getMinutesInWeekdays(personWorkingHours, weekdaysInPeriod);
			[minutesAllocated, minutesAvailable] = getAllocatedMinutes(
				personHeatMapData,
				holidaysIncludingDaysOff,
				personWorkingMinutes,
				dayData,
				minutesAllocated,
				minutesAvailable,
				startDate,
				endDate,
				pageComponent.state.todayDate,
				isInActualMode,
				person,
				lightWeightData.isUsingResourceService
			);
		}

		const dateString = `${timelineMinorStep}-${startDate}-${isInActualMode}`;
		pageComponent.companyUtilizationCache.set(dateString, {
			minutesAllocated,
			minutesAvailable,
		});
		items.push(
			new CompanyUtilizationItem({
				y: group.screenY,
				x: stepData.position,
				width: stepData.width,
				minutesAllocated,
				minutesAvailable,
				isPast: endDate < pageComponent.state.todayDate,
				isInActualMode: isInActualMode,
				dateString: dateString,
			})
		);
	}
	return items;
};

export const composePersonGroupUtilizationRowLightweight = (
	pageComponent,
	lightWeightData,
	group,
	stepDataArray,
	timelineStartDate,
	pixelsPerDay,
	timelineMinorStep
) => {
	const items = [];
	const {dayData} = pageComponent.props;
	let isInActualMode =
		!lightWeightData.company.isUsingSchedulingPlanMode && !lightWeightData.company.isUsingProjectAllocation;

	if (pageComponent.isCalculationSwitched) {
		isInActualMode = !isInActualMode;
	}

	for (const stepData of stepDataArray) {
		let minutesAllocated = 0;
		let minutesAvailable = 0;
		let {startDate, endDate} = stepData;

		for (const person of lightWeightData.persons) {
			if (!person.active) {
				continue;
			}
			if (personIsClientUser(person)) {
				continue;
			}
			if (
				lightWeightData.isUsingResourceService &&
				((person.startDate && endDate < person.startDate) || (person.endDate && startDate > person.endDate))
			) {
				continue;
			}
			if (!group.groups.find(group => group.id === person.id && group.filtered === false)) {
				continue;
			}

			const personWorkingHours = {
				monday: person.monday,
				tuesday: person.tuesday,
				wednesday: person.wednesday,
				thursday: person.thursday,
				friday: person.friday,
				saturday: person.saturday,
				sunday: person.sunday,
			};
			const personWorkingMinutes = [
				person.monday,
				person.tuesday,
				person.wednesday,
				person.thursday,
				person.friday,
				person.saturday,
				person.sunday,
			];
			const personHeatMapData = lightWeightData.heatMaps[person.id];
			const {holidaysIncludingDaysOff} = getHolidayDates(person, lightWeightData, personWorkingMinutes);
			const weekdaysInPeriod = getWeekdaysBetweenDates(startDate, endDate);
			for (const holidayDate of holidaysIncludingDaysOff.filter(date => date >= startDate && date <= endDate)) {
				weekdaysInPeriod[dayData[holidayDate].isoWeekday - 1]--;
			}
			for (let canvasDate = startDate; canvasDate <= endDate; canvasDate++) {
				if (!isDateWithinPersonStartEndDate(person, canvasDate)) {
					weekdaysInPeriod[dayData[canvasDate].isoWeekday - 1] = Math.max(
						weekdaysInPeriod[dayData[canvasDate].isoWeekday - 1] - 1,
						0
					);
				}
			}
			minutesAvailable += getMinutesInWeekdays(personWorkingHours, weekdaysInPeriod);
			[minutesAllocated, minutesAvailable] = getAllocatedMinutes(
				personHeatMapData,
				holidaysIncludingDaysOff,
				personWorkingMinutes,
				dayData,
				minutesAllocated,
				minutesAvailable,
				startDate,
				endDate,
				pageComponent.state.todayDate,
				isInActualMode,
				person,
				lightWeightData.isUsingResourceService
			);
		}

		const dateString = `${timelineMinorStep}-${startDate}-${isInActualMode}`;
		const cacheKey = `${group.groupId}-${dateString}`;
		pageComponent.personGroupUtilizationCache.set(cacheKey, {
			minutesAllocated,
			minutesAvailable,
		});
		items.push(
			new PersonGroupUtilizationItem({
				y: group.screenY,
				x: stepData.position,
				width: stepData.width,
				minutesAllocated,
				minutesAvailable,
				isPast: endDate < pageComponent.state.todayDate,
				isInActualMode: isInActualMode,
				dateString: dateString,
			})
		);
	}
	return items;
};

export const composePersonHeatMapRow = (
	pageComponent,
	lightWeightData,
	group,
	stepDataArray,
	timelineStartDate,
	pixelsPerDay,
	timelineMinorStep
) => {
	const person = group.data;
	const personWorkingHours = {
		monday: person.monday,
		tuesday: person.tuesday,
		wednesday: person.wednesday,
		thursday: person.thursday,
		friday: person.friday,
		saturday: person.saturday,
		sunday: person.sunday,
	};
	const personWorkingMinutes = [
		person.monday,
		person.tuesday,
		person.wednesday,
		person.thursday,
		person.friday,
		person.saturday,
		person.sunday,
	];
	const personHeatMapData = lightWeightData.heatMaps[person.id];
	const {holidaysIncludingDaysOff} = getHolidayDates(person, lightWeightData, personWorkingMinutes);
	const {todayDate} = pageComponent.state;
	const {dayData} = pageComponent.props;
	let isInActualMode =
		!lightWeightData.company.isUsingSchedulingPlanMode && !lightWeightData.company.isUsingProjectAllocation;
	if (pageComponent.isCalculationSwitched) {
		isInActualMode = !isInActualMode;
	}

	const heatMapItems = [];

	//Initialize heatmapCache if it does not exist
	if (!pageComponent.heatmapCache.has(person.id)) {
		pageComponent.heatmapCache.set(person.id, new Map());
	}
	const personCache = pageComponent.heatmapCache.get(person.id);

	for (const stepData of stepDataArray) {
		let minutesAllocated = 0;
		let {startDate, endDate} = stepData;

		if (
			lightWeightData.isUsingResourceService &&
			((person.startDate && endDate < person.startDate) || (person.endDate && startDate > person.endDate))
		) {
			continue;
		}

		const weekdaysInPeriod = getWeekdaysBetweenDates(startDate, endDate);
		for (const holidayDate of holidaysIncludingDaysOff.filter(date => date >= startDate && date <= endDate)) {
			weekdaysInPeriod[dayData[holidayDate].isoWeekday - 1]--;
		}
		for (let canvasDate = startDate; canvasDate <= endDate; canvasDate++) {
			if (!isDateWithinPersonStartEndDate(person, canvasDate)) {
				weekdaysInPeriod[dayData[canvasDate].isoWeekday - 1] = Math.max(
					weekdaysInPeriod[dayData[canvasDate].isoWeekday - 1] - 1,
					0
				);
			}
		}
		let minutesAvailable = getMinutesInWeekdays(personWorkingHours, weekdaysInPeriod);

		[minutesAllocated, minutesAvailable] = getAllocatedMinutes(
			personHeatMapData,
			holidaysIncludingDaysOff,
			personWorkingMinutes,
			dayData,
			minutesAllocated,
			minutesAvailable,
			startDate,
			endDate,
			pageComponent.state.todayDate,
			isInActualMode,
			person,
			lightWeightData.isUsingResourceService
		);
		minutesAvailable = Math.max(minutesAvailable, 0); // Available may never be negative

		const dateString = `${person.id}-${timelineMinorStep}-${startDate}-${isInActualMode}`;
		personCache.set(dateString, {minutesAllocated, minutesAvailable});

		const personIndex = lightWeightData.persons.findIndex(person => person.id === group.data.personId);

		heatMapItems.push(
			new HeatmapItem({
				y: group.screenY,
				x: stepData.position,
				intl: pageComponent.props.intl,
				width: stepData.width,
				minutesAllocated,
				minutesAvailable,
				groupHeight: group.totalHeight,
				isToday: todayDate >= startDate && todayDate <= endDate,
				eyeOptions: pageComponent.state.peopleSchedulingDetailSelectorOptions,
				onClick: pageComponent.onHeatmapItemClick.bind(
					pageComponent,
					person,
					startDate,
					endDate,
					minutesAllocated,
					minutesAvailable,
					new Map()
				),
				isPast: endDate < todayDate,
				isInActualMode: isInActualMode,
				personIndex,
			})
		);
	}

	return heatMapItems;
};

export const createLightWeightPersonGroupsAndItems = (pageComponent, props, lightWeightData, eyeOptions) => {
	const {formatMessage} = props.intl;
	const roleGroupsByRoleId = new Map();
	const groups = [];
	const items = [];
	const {roles, personLabels, departments, personSkills} = lightWeightData;

	const hasPersonGroupingFeature = lightWeightData.featureFlags.some(flag => flag.key === 'people_scheduling_grouping');
	const noGrouping = pageComponent.state.peopleSchedulingOptions.groupBy === GROUP_BY.NONE;
	const groupByRole = pageComponent.state.peopleSchedulingOptions.groupBy === GROUP_BY.ROLE;
	const groupByDepartment = pageComponent.state.peopleSchedulingOptions.groupBy === GROUP_BY.DEPARTMENT;

	const persons = lightWeightData.persons
		.sort((a, b) => {
			const aName = `${a.firstName || ''} ${a.lastName || ''}`.toLowerCase();
			const bName = `${b.firstName || ''} ${b.lastName || ''}`.toLowerCase();
			if (aName < bName) {
				return -1;
			}
			if (bName < aName) {
				return 1;
			}
			return 0;
		})
		.filter(person => {
			let showPerson = true;
			if (!hasPermission(PERMISSION_TYPE.SCHEDULING_ACCESS) && person.id !== lightWeightData.viewer.actualPersonId) {
				showPerson = false;
			}
			//Do not show client users and inactive people
			if (person.clientId || !person.active) {
				showPerson = false;
			}
			return showPerson;
		});

	if (hasPermission(PERMISSION_TYPE.SCHEDULING_ACCESS)) {
		groups.push(
			new CompanyUtilizationGroup({
				id: 'company-utilization',
				filtered: !eyeOptions.find(option => option.name === 'company-utilization').checked,
				composeItems: (group, stepDataArray, timelineStartDate, pixelsPerDay, timelineMinorStep) =>
					composeCompanyUtilizationRow(
						pageComponent,
						lightWeightData,
						group,
						stepDataArray,
						timelineStartDate,
						pixelsPerDay,
						timelineMinorStep
					),
				formatMessage,
				preventExpansion: !hasPersonGroupingFeature || noGrouping,
				onExpand: pageComponent.onExpandPersonGroups.bind(pageComponent),
			})
		);
	}

	for (const person of persons) {
		let roleName = '';
		if (person.roleId) {
			const role = roles.find(role => role.id === person.roleId);
			if (role) {
				roleName = role.name;
			}
		}
		let departmentName = '';
		if (person.departmentId) {
			const department = departments.find(department => department.id === person.departmentId);
			if (department) {
				departmentName = department.name;
			}
		}

		const labelIds = personLabels
			.filter(personLabel => personLabel.personId === person.id)
			.map(personLabel => personLabel.labelId);

		const skillIds = personSkills
			.filter(personSkill => personSkill.personId === person.id)
			.map(personSkill => personSkill.skillId);

		const personGroup = new PersonGroup({
			id: person.id,
			name: `${person.firstName == null ? '' : person.firstName} ${person.lastName == null ? '' : person.lastName}`,
			startDate: person.startDate,
			endDate: person.endDate,
			roleId: person.roleId || null,
			roleName,
			profilePictureId: person.profilePictureId,
			profilePictureDefaultId: person.profilePictureDefaultId,
			departmentId: person.departmentId,
			groups: [new LoadingGroup({})],
			composeItems: (group, stepDataArray, timelineStartDate, pixelsPerDay, timelineMinorStep) =>
				composePersonHeatMapRow(
					pageComponent,
					lightWeightData,
					group,
					stepDataArray,
					timelineStartDate,
					pixelsPerDay,
					timelineMinorStep
				),
			filterGroup: pageComponent.filterPersonGroupV4.bind(pageComponent),
			labelIds,
			skillIds,
			monday: person.monday,
			tuesday: person.tuesday,
			wednesday: person.wednesday,
			thursday: person.thursday,
			friday: person.friday,
			saturday: person.saturday,
			sunday: person.sunday,
			holidayCalendarId: person.holidayCalendarId,
			onPersonImageLoad: pageComponent.onPersonImageLoad.bind(pageComponent),
			isHeatMapDisabled: pageComponent.isHeatMapDisabled.bind(pageComponent),
		});

		if (hasPersonGroupingFeature && (groupByRole || groupByDepartment)) {
			personGroup.marginX = 16;
			for (const child of personGroup.groups) {
				child.marginX = 32;
			}

			const groupId = groupByRole
				? person.roleId
					? person.roleId
					: 'no-role'
				: person.departmentId
				? person.departmentId
				: 'no-department';
			const groupName = groupByRole
				? roleName
					? roleName
					: 'No Role'
				: departmentName
				? departmentName
				: 'No Department';

			if (!roleGroupsByRoleId.get(groupId)) {
				const personRoleGroup = new PersonGroupingGroup({
					id: groupId,
					name: groupName,
					groups: [personGroup],
					composeItems: (group, stepDataArray, timelineStartDate, pixelsPerDay, timelineMinorStep) =>
						composePersonGroupUtilizationRowLightweight(
							pageComponent,
							lightWeightData,
							group,
							stepDataArray,
							timelineStartDate,
							pixelsPerDay,
							timelineMinorStep
						),
					filterGroup: pageComponent.filterPersonGroupingGroupV4.bind(pageComponent),
				});
				roleGroupsByRoleId.set(groupId, personRoleGroup);
				groups.push(personRoleGroup);
			} else {
				roleGroupsByRoleId.get(groupId).groups.push(personGroup);
			}
		} else {
			groups.push(personGroup);
		}
	}

	groups.sort((a, b) => {
		const aName = (a.data.name || '').toLowerCase();
		const bName = (b.data.name || '').toLowerCase();
		if (aName < bName) {
			return -1;
		}
		if (bName < aName) {
			return 1;
		}
		return 0;
	});

	return {
		groups,
		items,
	};
};
